import type {
  TaskType,
  InnerFuncParamType,
  PrefetchApiPropsType,
  PrefetchApiPropItemType,
} from '../index'
import { createTaskConfigList } from './common'
import { handleTasks } from './handleTasks'

export async function handleOnServerSSR(param: InnerFuncParamType) {
  if (!process.server) {
    throw new Error('仅支持在 server 执行')
  }
  if (!param.ctx.req || !param.ctx.res) {
    throw new Error('BUG')
  }
  if (param.onPropChange) {
    // 无使用场景，未实现
    throw new Error('未实现 onPropChange')
  }

  const requestOnServer = require('./requestOnServer')

  const time = process.hrtime() // 计时 TODO 性能采集功能剥离

  //  检查，业务 propKey 不能覆盖 global
  Object.keys(param.pagePrefetchApi || {}).forEach((propKey) => {
    if (param.globalPrefetchApi && propKey in param.globalPrefetchApi) {
      throw new Error('业务 propKey，不能覆盖 global 任务')
    }
  })
  const allTasksConfig = createTaskConfigList(
    param.globalPrefetchApi,
    param.pagePrefetchApi
  )

  // 并行发起请求
  const taskMap: { [propKey: string]: TaskType } = requestOnServer.request(
    param.ctx.req,
    param.ctx.res,
    param.sitePrefetchApiConfig,
    allTasksConfig,
    { ...param.globalMock, ...param.pageMock }
  )

  // 不需要提前返回渲染，未提前生成 props。留待下方异步任务完成
  const propsResult: PrefetchApiPropsType = {}

  // 等待任务完成，内部实现 fallback、callback 流转
  await handleTasks(
    taskMap,
    () => {},
    () => {},
    (
      propKey: string,
      status: 'INITIAL' | 'LOADING' | 'CANCEL' | 'COMPLETE',
      data?: any
    ) => {
      // server 不处理 onPropChange，无使用场景
      // handleTasks 确保了，CANCEL 是终态，不会再流转回 LOADING
      // 上方已做参数检查拦截
      if (!propsResult[propKey]) {
        propsResult[propKey] = {} as PrefetchApiPropItemType
      }
      propsResult[propKey].status = status
      propsResult[propKey].value = data
      propsResult[propKey].isReady =
        status === 'CANCEL' || status === 'COMPLETE'
    },
    () => Object.keys(propsResult),
    !!param.pageFallback,
    (task: TaskType) => {
      if (!param.pageFallback) {
        throw new Error('BUG')
      }
      requestOnServer.fallback(
        param.ctx.req,
        param.ctx.res,
        param.sitePrefetchApiConfig,
        task,
        param.pageFallback
      )
      return (task.fallback as Promise<void>).then(() => void 0)
    },
    !!param.pageCallback,
    (task: TaskType) => {
      if (!param.pageCallback) {
        throw new Error('BUG')
      }
      // 此处 propResult 已经是 global+page，无需额外处理
      return param.pageCallback({ ...propsResult }, task.propKey, param.ctx)
    },
    param.convertRawResponse
  )

  // 已输出单个请求时间，不再统计成功失败比（涉及http语义和业务语义，不好识别）
  const diff = process.hrtime(time)
  const diffTime = diff[0] * 1e3 + diff[1] / 1e6

  // 即使无 request 也输出，其反应了 prefetchApi 机制消耗的资源
  param.ctx.res.header(
    'X-AL-PrefetchApi-Process-Time',
    `${diffTime.toFixed(3)}ms (${Object.keys(taskMap).length})`
  )

  // 通知已全部完成
  if (param.onComplete) {
    param.onComplete()
  }

  return propsResult
}

// export
// 返回 INITIAL 的初始 props，并返回配置
export async function handleOnServerExport(param: InnerFuncParamType) {
  if (!process.server) {
    throw new Error('仅支持在 server 执行')
  }
  if (param.onPropChange) {
    // 无使用场景，未实现
    throw new Error('未实现 onPropChange')
  }

  // 合并 global/page 任务
  // 检查，业务 propKey 不能覆盖 global
  Object.keys(param.pagePrefetchApi || {}).forEach((propKey) => {
    if (param.globalPrefetchApi && propKey in param.globalPrefetchApi) {
      throw new Error('业务 propKey，不能覆盖 global 任务')
    }
  })
  const pageTasksConfig = createTaskConfigList(
    param.globalPrefetchApi,
    param.pagePrefetchApi
  )

  const pageTasksMockData = { ...param.globalMock, ...param.pageMock }

  // 生成 INITIAL 的占位 props
  const resultProps: PrefetchApiPropsType = {}
  Object.keys(pageTasksConfig).forEach((propKey) => {
    resultProps[propKey] = {
      status: 'INITIAL',
      value: undefined,
      isReady: false,
    }
  })

  // 通知已全部完成
  if (param.onComplete) {
    param.onComplete()
  }
  return { resultProps, pageTasksConf: pageTasksConfig, pageTasksMockData }
}
